import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { CheckCircle, Shield, Clock, FileText, ChevronRight, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const caseStudies = [
  {
    slug: "troll",
    title: "Dealing with a Troll",
    description: "According to Wikipedia, a troll is someone that is deliberately disruptive to the site, often in order to inflame or invite conflict.",
    image: "https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/lb5cwv37_about-bg.webp"
  },
  {
    slug: "edit-war",
    title: "Ending a Multi-Year Edit-War",
    description: "A billion dollar corporation was in a protracted edit-war with a small business they compete with over the content of their Wikipedia page...",
    image: "https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/l803ts6t_punch.webp"
  },
  {
    slug: "new-page",
    title: "Creating a New Page",
    description: "Almost 20 years ago, two people started a website that started as a basic forum. Over the years, the website slowly grew into a job board...",
    image: "https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/ejcokl2a_movingup-3.webp"
  },
  {
    slug: "balancing",
    title: "Balancing a Controversial Page",
    description: "A large technology company had a Wikipedia page that focused almost exclusively on their controversies...",
    image: "https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/8745wi4r_sierrafarm-4.webp"
  }
];

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        canonical="/"
        description="Ethical Wiki provides professional Wikipedia consulting services. We help brands, executives, and organizations navigate Wikipedia ethically with expert guidance on page creation, editing, and reputation management."
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
              alt="Ethical Wiki" 
              className="w-10 h-10"
              width="40"
              height="40"
              loading="eager"
            />
            <span className="font-heading text-xl text-gray-900 font-bold tracking-tight">Ethical Wiki</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">About</a>
            <a href="#services" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">Services</a>
            <a href="#case-studies" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">Case Studies</a>
            <a href="#pricing" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">Pricing</a>
            <a href="#faq" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">FAQ</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden noise-overlay">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/20 to-transparent" />
        <motion.div 
          className="max-w-7xl mx-auto relative z-10"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.span 
            variants={fadeInUp}
            className="inline-block text-accent text-sm uppercase tracking-[0.3em] mb-6"
          >
            Wikipedia Engagement Done Right
          </motion.span>
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-5xl md:text-7xl text-text-primary max-w-4xl leading-tight mb-8"
          >
            Ethical<br />
            <span className="text-accent">Wikipedia</span><br />
            Engagement
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-text-secondary text-lg max-w-2xl mb-12 leading-relaxed"
          >
            Most Wikipedia firms edit the site in secret, violating Wikipedia's Terms of Use and the FTC's covert marketing regulations. Ethical Wiki offers an alternative for brands and individuals that want an honest approach to Wikipedia.
          </motion.p>
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button 
              onClick={() => navigate('/notability')}
              className="bg-accent text-black hover:bg-accent/90 font-semibold px-6 py-3"
              data-testid="hero-assessment-btn"
            >
              Get a Wikipedia Assessment
            </Button>
          </motion.div>
        </motion.div>
        
        {/* Background Image */}
        <div className="absolute right-0 top-0 w-1/2 h-full opacity-20">
          <img 
            src="https://images.pexels.com/photos/159711/books-bookstore-book-reading-159711.jpeg?auto=compress&cs=tinysrgb&w=1200&q=60" 
            alt="" 
            className="w-full h-full object-cover"
            width="600"
            height="800"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-background" />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-background-paper">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="grid md:grid-cols-2 gap-16 items-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div>
              <span className="text-accent text-sm uppercase tracking-[0.2em] mb-4 block">About Us</span>
              <h2 className="font-heading text-4xl text-text-primary mb-6">About Ethical Wiki</h2>
              <p className="text-text-secondary mb-8 leading-relaxed">
                Ethical Wiki is a boutique firm operated by David King. It serves startups, Fortune 500 companies, politicians, celebrities, non-profits, and others seeking transparent Wikipedia engagement.
              </p>
              
              <div className="grid grid-cols-3 gap-8">
                <div className="text-center">
                  <span className="font-heading text-4xl text-accent block">10+</span>
                  <span className="text-text-muted text-sm uppercase tracking-wider">Years Experience</span>
                </div>
                <div className="text-center">
                  <span className="font-heading text-4xl text-accent block">50K+</span>
                  <span className="text-text-muted text-sm uppercase tracking-wider">Edits Made</span>
                </div>
                <div className="text-center">
                  <span className="font-heading text-4xl text-accent block">30%+</span>
                  <span className="text-text-muted text-sm uppercase tracking-wider">Best Company Pages</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/rzk6lwjz_team.png" 
                alt="David King - Ethical Wiki" 
                className="w-full h-96 object-cover rounded-sm"
                loading="lazy"
                decoding="async"
                width="600"
                height="384"
              />
              <div className="absolute inset-0 border border-accent/30 translate-x-4 translate-y-4 -z-10 rounded-sm" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Three Steps Section */}
      <section id="services" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-accent text-sm uppercase tracking-[0.2em] mb-4 block">Our Approach</span>
            <h2 className="font-heading text-4xl text-text-primary">Three Steps to Ethical Wikipedia Engagement</h2>
          </div>
          
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div variants={fadeInUp}>
              <Card className="card-marketing p-8 h-full">
                <CardContent className="p-0">
                  <Shield className="w-12 h-12 text-accent mb-6" />
                  <h3 className="font-heading text-xl text-text-primary mb-4">Legal Compliance</h3>
                  <p className="text-text-secondary">
                    Be transparent about your vested interest in the page. Wikipedia's Terms of Use and FTC regulations prohibit brands from participating anonymously.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <Card className="card-marketing p-8 h-full">
                <CardContent className="p-0">
                  <FileText className="w-12 h-12 text-accent mb-6" />
                  <h3 className="font-heading text-xl text-text-primary mb-4">Wikipedia Policy</h3>
                  <p className="text-text-secondary">
                    Request edits rather than making them yourself. Wikipedia's Conflict of Interest policy requires that you request edits from the site's editors.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <Card className="card-marketing p-8 h-full">
                <CardContent className="p-0">
                  <CheckCircle className="w-12 h-12 text-accent mb-6" />
                  <h3 className="font-heading text-xl text-text-primary mb-4">Wikipedia's Ethos</h3>
                  <p className="text-text-secondary">
                    Do your best to contribute neutral content. Wikipedia's Arbitration Committee expects you to comply with the spirit of Wikipedia, not just the letter of its rules.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-24 px-6 bg-background-paper">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-accent text-sm uppercase tracking-[0.2em] mb-4 block">Success Stories</span>
            <h2 className="font-heading text-4xl text-text-primary mb-4">Case Studies</h2>
            <p className="text-text-secondary max-w-3xl mx-auto">
              Most of Ethical Wiki's work is focused on rewriting abandoned Wikipedia pages that didn't attract volunteer editors. However, the case studies below show how we've helped clients through some especially difficult situations.
            </p>
          </div>
          
          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {caseStudies.map((study, index) => (
              <motion.div key={study.slug} variants={fadeInUp}>
                <Link to={`/case-study/${study.slug}`} className="block group">
                  <Card className="card-marketing overflow-hidden h-full hover:border-accent/50 transition-all duration-300">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={study.image} 
                        alt={study.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="192"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-heading text-xl text-text-primary mb-2 group-hover:text-accent transition-colors">
                        {study.title}
                      </h3>
                      <p className="text-text-secondary text-sm mb-4 line-clamp-2">
                        {study.description}
                      </p>
                      <span className="inline-flex items-center text-accent text-sm font-medium group-hover:gap-2 transition-all">
                        Read case study
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6 bg-background-paper">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-accent text-sm uppercase tracking-[0.2em] mb-4 block">Investment</span>
            <h2 className="font-heading text-4xl text-text-primary">Flat-Fee Pricing</h2>
            <p className="text-text-secondary mt-4">One year of service for a flat fee</p>
          </div>
          
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div variants={fadeInUp}>
              <Card className="card-marketing p-8 h-full relative overflow-hidden">
                <CardContent className="p-0">
                  <h3 className="text-text-secondary text-sm uppercase tracking-wider mb-2">Long-term support</h3>
                  <h4 className="font-heading text-2xl text-text-primary mb-4">Maintenance</h4>
                  <div className="flex items-baseline mb-8">
                    <span className="font-heading text-5xl text-accent">$1,200</span>
                  </div>
                  <ul className="space-y-3 text-text-secondary">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Monitor the Wikipedia page
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Help respond to bad edits
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Support updates
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Consult as issues arise
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <Card className="card-marketing p-8 h-full relative overflow-hidden">
                <CardContent className="p-0">
                  <h3 className="text-text-secondary text-sm uppercase tracking-wider mb-2">Submit a draft to Wikipedia</h3>
                  <h4 className="font-heading text-2xl text-text-primary mb-4">New Page</h4>
                  <div className="flex items-baseline mb-8">
                    <span className="font-heading text-5xl text-accent">$2,400</span>
                  </div>
                  <ul className="space-y-3 text-text-secondary">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Research, write and wiki-code a draft
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Consult on Wikipedia rules during review
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Coach client through submission
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Maintenance services for one year
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <Card className="card-marketing p-8 h-full relative overflow-hidden">
                <CardContent className="p-0">
                  <h3 className="text-text-secondary text-sm uppercase tracking-wider mb-2">A dramatic rework of a derelict page</h3>
                  <h4 className="font-heading text-2xl text-text-primary mb-4">Overhaul</h4>
                  <div className="flex items-baseline mb-8">
                    <span className="font-heading text-5xl text-accent">$3-9k</span>
                  </div>
                  <ul className="space-y-3 text-text-secondary">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Research, write and wiki-code a draft
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Consult on Wikipedia rules during review
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Coach client through submission
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      Maintenance services for one year
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-accent text-sm uppercase tracking-[0.2em] mb-4 block">Support</span>
            <h2 className="font-heading text-4xl text-text-primary">Frequently Asked Questions</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="card-marketing border-none">
              <AccordionTrigger className="px-6 py-4 text-left text-text-primary hover:text-accent">
                How can I tell if a Wikipedia firm is actually ethical?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-text-secondary">
                Many Wikipedia services advertise they are ethical even though they are banned from Wikipedia for illegal, covert practices. Luckily, Wikipedia itself has a list of vendors that meet two basic criteria: (1) Wikipedians know where we are on Wikipedia (2) and Wikipedians have not blocked us. Use a vendor from that list to avoid falling for scams.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="card-marketing border-none">
              <AccordionTrigger className="px-6 py-4 text-left text-text-primary hover:text-accent">
                Why participate ethically when it's so much harder?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-text-secondary">
                Using unethical practices often garner short-term results at a long-term cost. Even if your changes are not immediately reverted, often a badge of shame is later added to the page, publicly advertising your misconduct to readers. Ethical Wiki is often brought in to repair things after someone has already created a bad situation using a black-hat vendor.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="card-marketing border-none">
              <AccordionTrigger className="px-6 py-4 text-left text-text-primary hover:text-accent">
                How long does it take?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-text-secondary">
                Wikipedians are volunteers that are not beholden to any particular timetable. Sometimes, very large projects are approved by editors within 24 hours. Other times, we spend months following up on obvious changes. Three months is a good general timetable to circulate internally.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="card-marketing border-none">
              <AccordionTrigger className="px-6 py-4 text-left text-text-primary hover:text-accent">
                Can I write my own content and have Ethical Wiki post it?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-text-secondary">
                Sometimes clients insist on writing their own content for Ethical Wiki to review and provide feedback on. This approach has a 100% failure rate. It almost always doubles the budget when the client eventually hires Ethical Wiki to write the content.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5" className="card-marketing border-none">
              <AccordionTrigger className="px-6 py-4 text-left text-text-primary hover:text-accent">
                How do I get started?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-text-secondary">
                Schedule a consultation to discuss what changes you are looking for and get advice on how Wikipedia's rules apply to your situation. Once you have settled on what changes you want to pursue, Mr. King will provide a Wikipedia Assessment that includes a quote.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-6" className="card-marketing border-none">
              <AccordionTrigger className="px-6 py-4 text-left text-text-primary hover:text-accent">
                What if I want to create a new page?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-text-secondary">
                In most cases, Ethical Wiki advises against creating new pages. Wikipedia is pretty reliable about starting a page on those topics that are famous enough to meet Wikipedia's criteria. However, if you are looking to submit a new page, we recommend using our New Page Assessment tool to get a free assessment from Mr. King on whether your topic is famous enough to qualify for a page.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-background-paper relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.pexels.com/photos/256541/pexels-photo-256541.jpeg?auto=compress&cs=tinysrgb&w=1200&q=60" 
            alt="" 
            className="w-full h-full object-cover"
            width="1200"
            height="800"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-5xl text-text-primary mb-6">Get Started</h2>
          <p className="text-text-secondary text-lg mb-12 max-w-2xl mx-auto">
            Start your assessment today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/notability')}
              className="bg-accent text-black hover:bg-accent/90 font-semibold px-6 py-3"
              data-testid="cta-assessment-btn"
            >
              Get a Wikipedia Assessment
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
              alt="Ethical Wiki" 
              className="w-6 h-6"
              width="24"
              height="24"
              loading="lazy"
            />
            <span className="text-gray-600 text-sm">© 2025 Ethical Wiki. All rights reserved.</span>
          </div>
          <nav className="flex gap-6">
            <a href="#about" className="text-gray-600 hover:text-accent text-sm">About</a>
            <a href="#services" className="text-gray-600 hover:text-accent text-sm">Services</a>
            <a href="#case-studies" className="text-gray-600 hover:text-accent text-sm">Case Studies</a>
            <a href="#pricing" className="text-gray-600 hover:text-accent text-sm">Pricing</a>
            <a href="#faq" className="text-gray-600 hover:text-accent text-sm">FAQ</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
